import { useLanguage } from '../i18n';
import { useOrganizations } from '../context/useOrganizations';
import { useApp } from '../context/useApp';
import { Plus, Trash2, ArrowRight, Building2 } from 'lucide-react';

interface OrganizationsDashboardProps {
  onCreate: () => void;
  onOpen: (orgId: string) => void;
  onBack: () => void;
}

export default function OrganizationsDashboard({ onCreate, onOpen, onBack }: OrganizationsDashboardProps) {
  const { t } = useLanguage();
  const d = t.org;
  const { organizations, deleteOrganization } = useOrganizations();
  const { projects } = useApp();

  return (
    <div className="h-full overflow-auto p-4 sm:p-6 md:p-8 safe-bottom">
      <div className="max-w-6xl mx-auto">
        <div className="flex items-start justify-between mb-6 sm:mb-8 gap-3 sm:gap-6 flex-wrap sm:flex-nowrap">
          <div className="min-w-0 flex-1">
            <button
              type="button"
              onClick={onBack}
              className="text-sm text-slate hover:text-ink mb-2 inline-flex items-center gap-1"
            >
              ← {d.back}
            </button>
            <h1 className="text-2xl sm:text-3xl font-display font-bold text-ink">{d.title}</h1>
            <p className="text-slate mt-1 text-sm sm:text-base max-w-xl">{d.subtitle}</p>
          </div>
          <button
            onClick={onCreate}
            className="flex-1 sm:flex-none min-h-[44px] px-4 py-2.5 bg-red-500 hover:bg-red-600 text-white font-medium transition-colors flex items-center justify-center gap-2"
          >
            <Plus size={18} />
            {d.newCta}
          </button>
        </div>

        {organizations.length === 0 ? (
          <div className="bg-white border border-cloud p-8 sm:p-12 text-center">
            <div className="w-16 h-16 bg-red-50 flex items-center justify-center mx-auto mb-4">
              <Building2 size={32} className="text-red-500" />
            </div>
            <h2 className="text-xl font-semibold mb-2">{d.emptyTitle}</h2>
            <p className="text-slate mb-6 max-w-md mx-auto">{d.emptyDescription}</p>
            <button
              onClick={onCreate}
              className="min-h-[44px] px-6 py-3 bg-red-500 hover:bg-red-600 text-white font-medium transition-colors"
            >
              {d.emptyCta}
            </button>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
            {organizations.map((org) => {
              const linked = (org.linkedProjectIds || [])
                .map((id) => projects.find((p) => p.id === id))
                .filter(Boolean);
              return (
                <div
                  key={org.id}
                  className="bg-white border border-cloud p-4 sm:p-6 hover:border-red-300 hover:shadow-sm transition-all cursor-pointer group"
                  onClick={() => onOpen(org.id)}
                >
                  <div className="flex items-start justify-between mb-4">
                    <div className="w-10 h-10 bg-red-50 flex items-center justify-center text-red-600">
                      <Building2 size={20} />
                    </div>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        if (confirm(d.deleteConfirm(org.name))) deleteOrganization(org.id);
                      }}
                      className="p-2 text-ash hover:text-red-500 hover:bg-red-50 min-w-[44px] min-h-[44px] flex items-center justify-center"
                      title="Delete organization"
                      aria-label={`Delete ${org.name}`}
                    >
                      <Trash2 size={16} />
                    </button>
                  </div>
                  <h3 className="font-semibold text-lg mb-1 truncate">{org.name}</h3>
                  <p className="text-sm text-slate mb-4 line-clamp-2">{org.idea || '—'}</p>
                  <div className="flex items-center justify-between text-xs text-ash border-t border-mist pt-3 gap-2">
                    <span className="font-medium truncate">{org.industry || d.industryFallback}</span>
                    <span className="shrink-0">{new Date(org.updatedAt).toLocaleDateString()}</span>
                  </div>
                  {linked.length > 0 && (
                    <div className="mt-3 text-xs text-slate">
                      {linked.length} {linked.length === 1 ? d.linkedProjectSingular : d.linkedProjectPlural}
                    </div>
                  )}
                  <div className="mt-4 flex items-center text-sm font-medium text-red-500 sm:opacity-0 group-hover:opacity-100 transition-opacity">
                    {d.open}
                    <ArrowRight size={16} className="ml-1" />
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
